import { createApi } from "@reduxjs/toolkit/query/react";
import { baseWidgetQuery } from "@/store/features/widget/baseWidgetQuery";
import { baseWidgetBoQuery } from "@/store/features/widget/baseWidgetBoQuery";
import { Chat } from "@/services/models/chat";
import { chatUISliceActions } from "@/store/features/chat/chatUiSlice";
import apiClient from "@/services/api/apiClient";
import { chatApi } from "@/store/features/chat/chatApiSlice";


export const widgetApi = createApi({
    reducerPath: 'widgetApi',
    baseQuery: baseWidgetQuery,
    endpoints: (builder) => ({
        getSession: builder.query<Chat, void>({
            query: () => ({
                url: '/Embed/widget/session',
                method: 'GET',
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;

                    if (data) {
                        dispatch(chatUISliceActions.setChat({
                            chat: data,
                            project: null
                        }));

                        if (data.collection_id) {
                            window.history.pushState(null, '', `/widget/${data.collection_id}`);
                            dispatch(chatApi.util.invalidateTags(['History']));
                        }
                    }
                } catch (error) {
                    console.error('Error in getSession query:', error);
                }
            }
        }),
        createChat: builder.mutation<Chat, void>({
            query: () => ({
                url: `Embed/widget/chat/new`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
            }),
        }),
        postNewMessage: builder.mutation<string, { chatId: string, agent: string, message: string }>({
            queryFn: async ({ message, chatId, agent }, { dispatch, signal }) => {
                try {
                    dispatch(chatUISliceActions.addUserMessages({ message }));
                    await apiClient.logChunksAndReturnFullResponse(
                        chatId,
                        message,
                        [],
                        agent,
                        {
                            onChunk: chunk => {
                                console.log('[widgetApiSlice] onChunk received:', chunk);
                                if (chunk.toolExecution) {
                                    console.log('[widgetApiSlice] Dispatching addToolExecution for:', chunk.toolExecution.toolName, 'status:', chunk.toolExecution.status);
                                    // Dispatch immediately - Redux Toolkit will handle batching, but we ensure each update is processed
                                    dispatch(chatUISliceActions.addToolExecution(chunk.toolExecution));
                                } else if (chunk.message !== undefined) {
                                    console.log('[widgetApiSlice] Dispatching addMessageChunk');
                                    dispatch(chatUISliceActions.addMessageChunk(chunk.message));
                                } else {
                                    console.log('[widgetApiSlice] Chunk has neither toolExecution nor message');
                                }
                            },
                            onFinished: (response) => {
                                dispatch(chatUISliceActions.finishAiMessage(response.messageId));
                            },
                        },
                        signal,
                        true
                    );
                    return { data: "" };
                } catch (error) {
                    console.log("Caught error on postNewMessage", error);
                    dispatch(chatUISliceActions.resetAiMessageError());
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            data: error instanceof Error ? error.message : 'An unknown error occurred',
                            error: 'API Error'
                        }
                    };
                }
            }
        })
    }),
});


export const widgetBoApi = createApi({
    reducerPath: 'widgetBoApi',
    baseQuery: baseWidgetBoQuery,
    tagTypes: ['BoUser', 'BoHistory', 'BoMessages'],
    endpoints: (builder) => ({
        // Get current BO user info
        getUserBo: builder.query<any, void>({
            query: () => ({
                url: '/Embed/widget-bo/user',
                method: 'GET',
            }),
            providesTags: ['BoUser'],
        }),

        getChatHistoryBo: builder.query<any[], void>({
            query: () => ({
                url: '/Embed/widget-bo/history',
                method: 'GET',
            }),
            transformResponse: (response: any[]) => {
                return response.map(group => ({
                    label: group.label,
                    chats: (group.chats || []).map((chat: any) => ({
                        collection_id: chat.collectionId || chat.collection_id || '',
                        description: chat.description || '',
                        project_id: chat.projectId || chat.project_id || '',
                        user_oid: chat.userId || chat.user_oid || '',
                        last_message_time: chat.lastMessageTime || chat.last_message_time || '',
                        deleted: chat.deleted || false,
                    }))
                }));
            },
            providesTags: ['BoHistory'],
        }),

        getChatMessagesBo: builder.query<{ chat: Chat, project: any }, string>({
            query: (chatId) => ({
                url: `/Embed/widget-bo/chat/${chatId}`,
                method: 'GET',
            }),
            transformResponse: (response: any) => {
                const chatData = response.chat || response;
                const transformedChat: Chat = {
                    collection_id: chatData.collectionId || chatData.collection_id || '',
                    description: chatData.description || '',
                    project_id: chatData.projectId || chatData.project_id || '',
                    user_oid: chatData.userId || chatData.user_oid || '',
                    last_message_time: chatData.lastMessageTime || chatData.last_message_time || new Date().toISOString(),
                    messages: (chatData.messages || []).map((msg: any) => ({
                        id: msg.id,
                        message: msg.message || '',
                        type: msg.type,
                        time: msg.time,
                        images: msg.images || [],
                        vote: msg.vote ?? null,
                        agent: msg.agent,
                        session_id: chatData.collectionId || chatData.collection_id,
                        inputTokens: msg.inputTokens,
                        outputTokens: msg.outputTokens,
                    })),
                };

                return {
                    chat: transformedChat,
                    project: response.project || null
                };
            },
            providesTags: (result, error, chatId) => [{ type: 'BoMessages', id: chatId }],
            async onQueryStarted(chatId, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(chatUISliceActions.setChat({
                        chat: data.chat,
                        project: data.project || null
                    }));
                } catch (error) {
                    console.error('Error loading chat messages:', error);
                }
            }
        }),

        getSessionBo: builder.query<Chat, void>({
            query: () => ({
                url: '/Embed/widget/session',
                method: 'GET',
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;

                    if (data) {
                        dispatch(chatUISliceActions.setChat({
                            chat: data,
                            project: null
                        }));

                        if (data.collection_id) {
                            window.history.pushState(null, '', `/widget-bo/${data.collection_id}`);
                            dispatch(chatApi.util.invalidateTags(['History']));
                        }
                    }
                } catch (error) {
                    console.error('Error in getSessionBo query:', error);
                }
            }
        }),
        createChatBo: builder.mutation<Chat, void>({
            query: () => ({
                url: `/Embed/widget-bo/chat/new`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: {
                    projectId: '01JFGEEM0KKJ0N40CJWKHT37ZA' // Default project for Widget-BO
                }
            }),
            transformResponse: (response: any) => {
                return {
                    collection_id: response.collectionId || response.collection_id || '',
                    description: response.description || '',
                    project_id: response.projectId || response.project_id || '',
                    user_oid: response.userId || response.user_oid || '',
                    last_message_time: response.lastMessageTime || response.last_message_time || new Date().toISOString(),
                    messages: response.messages || [],
                } as Chat;
            },
            invalidatesTags: ['BoHistory'],
        }),
        postNewMessageBo: builder.mutation<string, { chatId: string, agent: string, message: string, files?: string[] }>({
            queryFn: async ({ message, chatId, agent, files }, { dispatch, signal }) => {
                try {
                    dispatch(chatUISliceActions.addUserMessages({ message }));
                    await apiClient.logChunksAndReturnFullResponse(
                        chatId,
                        message,
                        files ?? [],
                        agent,
                        {
                            onChunk: chunk => {
                                console.log('[widgetApiSlice] onChunk received:', chunk);
                                if (chunk.toolExecution) {
                                    console.log('[widgetApiSlice] Dispatching addToolExecution for:', chunk.toolExecution.toolName, 'status:', chunk.toolExecution.status);
                                    // Dispatch immediately - Redux Toolkit will handle batching, but we ensure each update is processed
                                    dispatch(chatUISliceActions.addToolExecution(chunk.toolExecution));
                                } else if (chunk.message !== undefined) {
                                    console.log('[widgetApiSlice] Dispatching addMessageChunk');
                                    dispatch(chatUISliceActions.addMessageChunk(chunk.message));
                                } else {
                                    console.log('[widgetApiSlice] Chunk has neither toolExecution nor message');
                                }
                            },
                            onFinished: (response) => {
                                dispatch(chatUISliceActions.finishAiMessage(response.messageId));
                            },
                        },
                        signal,
                        false,
                        true
                    );
                    return { data: "" };
                } catch (error) {
                    console.log("Caught error on postNewMessageBo", error);
                    dispatch(chatUISliceActions.resetAiMessageError());
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            data: error instanceof Error ? error.message : 'An unknown error occurred',
                            error: 'API Error'
                        }
                    };
                }
            },
            invalidatesTags: (result, error, { chatId }) => [{ type: 'BoMessages', id: chatId }],
        }),
        softDeleteChatBo: builder.mutation<void, string>({
            query: (chatId) => ({
                url: `/Chat/${chatId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['BoHistory'],
        }),
        postVoteBo: builder.mutation<void, { messageId: number; vote: number }>({
            query: ({ messageId, vote }) => ({
                url: `Embed/widget-bo/message/${messageId}/vote`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(vote)
            }),
        }),

        checkBoAccess: builder.query<{ hasAccess: boolean }, void>({
            query: () => '/Embed/widget-bo/access-check',
        }),
    }),
});

export const { useGetSessionQuery, usePostNewMessageMutation, useCreateChatMutation } = widgetApi;

export const {
    useGetUserBoQuery,
    useGetChatHistoryBoQuery,
    useGetChatMessagesBoQuery,
    useGetSessionBoQuery,
    usePostNewMessageBoMutation,
    useCreateChatBoMutation,
    useSoftDeleteChatBoMutation,
    usePostVoteBoMutation,
    useCheckBoAccessQuery,
    useLazyCheckBoAccessQuery,
} = widgetBoApi;